import {getPriceData} from '@salesforce/retail-react-app/app/utils/product-utils'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'

export const transformGiftCertificateItems = (giftCertificateItems) => {
    if (!giftCertificateItems || giftCertificateItems.length === 0) return []
    return giftCertificateItems.map((item) => ({
        id: item.giftCertificateItemId,
        name: `Cavender's E-Gift Card`,
        price: item?.amount || item?.grossPrice,
        quantity: 1,
        imageUrl: getAssetUrl('static/img/header/EGiftCard.png'),
        isGiftCertificate: true,
        recipientName: item.recipientName,
        recipientEmail: item.recipientEmail,
        senderName: item.senderName,
        message: item.message,
        productUrl: ''
    }))
}

export const transformBasketData = (products, basket, statesMapping) => {
    if (!basket?.productItems || basket.productItems.length === 0) {
        return []
    }
    if (!products) {
        return []
    }
    let basketItems = []

    basket.productItems.forEach((basketItem, index) => {
        const product = products[basketItem.productId]
        if (!product) {
            return
        }

        const {
            id,
            name,
            price,
            currency,
            variationValues,
            tieredPrices,
            c_cloudinary,
            c_primaryCatergoryID,
            primaryCategoryId,
            c_style,
            c_selectedVariationSize,
            c_selectedStoreObj,
            c_shipToHomeOnPWA,
            c_instorePickupOnPWA,
            slugUrl,
            variationAttributes,
            imageGroups,
            c_productPromotionsDetails,
            type,
            c_AvailableForShipToStore,
            c_availableForInStorePickup,
            inventory,
            stockLevel
        } = product

        const productUrl = slugUrl ? new URL(slugUrl).pathname : ''

        const productPromotions = Array.isArray(basketItem?.priceAdjustments)
            ? basketItem.priceAdjustments.map((item) => {
                  return item?.itemText
              })
            : []

        // Get price data to extract listPrice (original price from tieredPrices)
        const priceData = getPriceData(product, {})
        // listPrice is the original/highest price from tieredPrices, fallback to product.price
        const catalogOriginalPrice = priceData?.listPrice || parseFloat(price) || 0

        // Basket prices (may already be discounted)
        const basketOriginalPrice = parseFloat(basketItem?.price) || 0
        // priceAfterItemDiscount is a SFCC line total (quantity × effective per-unit price after
        // all item-level promotions like BOGO). Dividing by quantity gives the true per-unit
        // effective price and is the most reliable source for discounted price.
        // c_finalPrice may reflect catalog price and not account for quantity-based promos,
        // so it is used only as a fallback when priceAfterItemDiscount is unavailable.
        const basketDiscountedPrice =
            basketItem?.priceAfterItemDiscount != null
                ? parseFloat(basketItem.priceAfterItemDiscount) / (basketItem.quantity || 1)
                : basketItem?.c_finalPrice != null && basketItem.c_finalPrice !== ''
                ? parseFloat(basketItem.c_finalPrice)
                : basketOriginalPrice

        // Determine basePrice (original price) - always prioritize catalog price
        // The catalog price is the true original price before any discounts
        // Only use basket price as fallback if catalog price is not available
        let basePrice = catalogOriginalPrice
        if (basePrice <= 0) {
            // If catalog price is not available, try to infer from basket prices
            // If basket price > discounted price, basket price might be the original
            if (basketOriginalPrice > basketDiscountedPrice && basketOriginalPrice > 0) {
                basePrice = basketOriginalPrice
            } else {
                // Last resort: use discounted price (no discount scenario)
                basePrice = basketDiscountedPrice
            }
        }

        // finalPrice is the per-unit discounted price for product display (item-level discounts only).
        // Order-level discounts (source codes) should NOT affect product tile prices.
        const finalPrice = basketDiscountedPrice

        const productItem = {
            id: `${id}_${index}`,
            originalProductId: id,
            name,
            price: `${basePrice.toFixed(2)}`,
            priceAfterItemDiscount: basketItem?.priceAfterItemDiscount,
            quantity: basketItem?.quantity,
            c_freightSurcharge: basketItem?.c_freightSurcharge || '',
            tieredPrices,
            currency,
            variationValues,
            isPhysicalGiftCard: c_style === 'GIFT',
            imageUrl: c_cloudinary?.miniCartImage?.url || c_cloudinary?.url || null,
            priceData: getPriceData(product, {}),
            productUrl,
            c_primaryCatergoryID,
            primaryCategoryId,
            c_selectedVariationSize,
            c_consolidateDeliveries: basket?.c_consolidateDeliveries || '',
            c_restrictionList: basket?.c_restrictionList || '',
            isRestricted: basket?.c_restrictionList ? basket.c_restrictionList.includes(id) : false,
            c_storeId: basketItem?.c_selectedStoreId || '',
            c_selectedStoreObj,
            showShipToHome: basketItem?.c_storepickup === false ? true : false,
            showInStorePickup:
                basketItem?.c_storepickup === true && basketItem?.c_selectedStoreId ? true : false,
            c_cloudinary,
            variationAttributes,
            imageGroups,
            c_productPromotionsDetails,
            shippingState: basket?.billingAddress?.city || '',
            type,
            c_AvailableForShipToStore,
            c_availableForInStorePickup,
            c_storepickup: basketItem?.c_storepickup || false,
            shippingMethod: basketItem?.c_storepickup ? 'store-pickup' : 'ship-to-home',
            basketItem: basketItem,
            itemId: basketItem?.itemId || `${basketItem.productId}_${index}`,
            adjustedTax: basketItem?.adjustedTax,
            // basePrice is the original catalog price (for showing crossed-out price)
            // Use catalog price as original, which is the true original before any discounts
            // Fallback to basket price if catalog price is not available
            basePrice: basePrice,
            // finalPrice is the discounted price from basket (priceAfterItemDiscount)
            // This is what the customer actually pays
            finalPrice: finalPrice,
            bonusProductLineItem: basketItem?.bonusProductLineItem,
            c_promotionFlag: basketItem?.c_promotionFlag,
            c_prop65Warning: basketItem?.c_prop65Warning,
            c_selectedStoreId: basketItem?.c_selectedStoreId,
            c_warehouseFulfillmentOnly: basketItem?.c_warehouseFulfillmentOnly,
            gift: basketItem?.gift,
            inventoryId: basketItem?.inventoryId,
            itemText: basketItem?.itemText,
            priceAfterOrderDiscount: basketItem?.priceAfterOrderDiscount,
            productName: basketItem?.productName,
            shipmentId: basketItem?.shipmentId,
            tax: basketItem?.tax,
            taxBasis: basketItem?.taxBasis,
            taxClassId: basketItem?.taxClassId,
            taxRate: basketItem?.taxRate,
            inventory,
            stockLevel,
            productPromotions
        }

        basketItems.push(productItem)
    })

    return basketItems.reverse()
}
